import { useEffect, useState } from "react";
import { DatePicker, Flex, InputNumber, Modal, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { IntegrationLockConfirmPayload } from "../integrationRowLockTypes";

const { Text } = Typography;

export type IntegrationLockUntilModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (payload: IntegrationLockConfirmPayload) => void | Promise<void>;
  title?: string;
  confirmLoading?: boolean;
};

export function IntegrationLockUntilModal({
  open,
  onCancel,
  onConfirm,
  title = "Pausa synk till datum",
  confirmLoading = false,
}: IntegrationLockUntilModalProps) {
  const [dateValue, setDateValue] = useState<Dayjs | null>(() => dayjs().add(7, "day"));
  const [stockValue, setStockValue] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setDateValue(dayjs().add(7, "day"));
      setStockValue(null);
    }
  }, [open]);

  const handleOk = async () => {
    if (!dateValue) return;
    try {
      await Promise.resolve(
        onConfirm({
          lockedUntilIso: dateValue.endOf("day").toISOString(),
          pausedStockQuantity: stockValue,
        }),
      );
      onCancel();
    } catch {
      /* Felmeddelande från anroparen */
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Lås"
      destroyOnClose
      confirmLoading={confirmLoading}
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Text type="secondary" style={{ display: "block", fontSize: 14, lineHeight: 1.5 }}>
            Välj sista dag integrationssynken ska vara pausad. Därefter återgår raden till normal synk.
          </Text>
          <DatePicker
            value={dateValue}
            onChange={setDateValue}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) => !!current && current < dayjs().startOf("day")}
          />
        </Flex>
        <Flex vertical gap={8}>
          <Text strong style={{ display: "block" }}>
            Pausat lagerantal
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: 14, lineHeight: 1.5 }}>
            Ange saldo som ska gälla medan låset är aktivt. Lämna tomt om du vill att saldo ska synkas normalt.
          </Text>
          <InputNumber
            value={stockValue}
            onChange={(v) => setStockValue(typeof v === "number" ? v : null)}
            style={{ width: "100%" }}
            min={0}
            precision={0}
            placeholder="Valfritt — t.ex. aktuellt saldo"
          />
        </Flex>
      </Flex>
    </Modal>
  );
}
